import { type FC, type PropsWithChildren } from 'react'
import { Badge, CloseButton, Form } from 'react-bootstrap'
import classNames from 'classnames'
import { Button } from '../../../../components/Button/Button'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { NotificationChannel, type ReminderSettings } from '../../../../models/Scheduling'
import { SettingsCard } from './SettingsSection'
import styles from './SchedulingSettings.module.scss'
import {
  createRowDraft,
  formatReminderOffset,
  hasRowWithoutChannel,
  hasSmsEntry,
  withoutSmsChannel,
  MAX_REMINDER_ENTRIES,
  nextReminderOffset,
  sameChannels,
  settingsChanged,
  type ReminderRowDraft,
  type ReminderSettingsDraft,
} from './reminderDrafts'

const NEW_ROW_CAVEAT = "New steps only apply to send times that haven't passed yet."

export type SmsChannelState = 'hidden' | 'off' | 'on'

const CHANNEL_OPTIONS = [
  { channel: NotificationChannel.EMAIL, label: 'Email' },
  { channel: NotificationChannel.SMS, label: 'Text' },
]

const channelsLabel = (channels: NotificationChannel[]): string => {
  if (!channels.includes(NotificationChannel.SMS)) {
    return 'email'
  }
  return channels.includes(NotificationChannel.EMAIL) ? 'email and text' : 'text'
}

export interface ReminderSettingsCopy {
  title: string
  heading: string
  description: string
  banner?: string
  rowLabel: string
  trailingCopy: string
  channelsTrailingCopy?: string
  addLabel: string
  removeLabel: string
  removeLastTooltip: string
}

export interface ReminderSettingsCardProps {
  copy: ReminderSettingsCopy
  settings: ReminderSettings
  draft: ReminderSettingsDraft | null
  smsChannel: SmsChannelState
  templatesLabel?: string
  readOnly: boolean
  editDisabled: boolean
  editDisabledTooltip: string
  isSaving: boolean
  error: string | null
  onEdit: () => void
  onCancel: () => void
  onSave: () => void
  onDraftChange: (draft: ReminderSettingsDraft) => void
  onShowMessageTemplates: () => void
  onShowTextMessages: () => void
}

const TextsOffWarning: FC<PropsWithChildren> = ({ children }) => (
  <div className='d-flex align-items-start gap-2 paragraph-small zocks-text-gray-900'>
    <i aria-hidden='true' className='bi bi-exclamation-circle flex-shrink-0 zocks-text-warning-dark' />
    <span>{children}</span>
  </div>
)

interface ReadOnlyRowsProps {
  copy: ReminderSettingsCopy
  settings: ReminderSettings
  showChannels: boolean
  textsOff: boolean
}

const ReadOnlyRows: FC<ReadOnlyRowsProps> = ({ copy, settings, showChannels, textsOff }) => (
  <div className={classNames(styles.reminderTimeline, 'd-flex flex-column gap-2')}>
    {settings.entries.map((entry) => (
      <div key={entry.id ?? entry.offsetMinutes} className='d-flex align-items-center gap-2'>
        <span className={styles.reminderTimelineDot} />
        <div className='flex-grow-1 d-flex flex-column gap-2 zocks-bg-color-gray-100 rounded-2 p-3'>
          <span>
            {copy.rowLabel} <span className='bold'>{formatReminderOffset(entry.offsetMinutes)}</span>{' '}
            {showChannels && copy.channelsTrailingCopy != null ? (
              <>
                {copy.channelsTrailingCopy} <span className='bold'>{channelsLabel(entry.channels)}</span>.
              </>
            ) : (
              copy.trailingCopy
            )}
          </span>
          {textsOff && entry.channels.includes(NotificationChannel.SMS) ? (
            <TextsOffWarning>
              {entry.channels.includes(NotificationChannel.EMAIL) ? 'Only the email goes out.' : "This reminder doesn't go out."}
            </TextsOffWarning>
          ) : null}
        </div>
      </div>
    ))}
  </div>
)

interface ChannelCheckboxesProps {
  row: ReminderRowDraft
  index: number
  smsDisabled: boolean
  onChange: (channels: NotificationChannel[]) => void
}

const ChannelCheckboxes: FC<ChannelCheckboxesProps> = ({ row, index, smsDisabled, onChange }) => (
  <div className='d-flex align-items-center gap-3'>
    {CHANNEL_OPTIONS.map(({ channel, label }) => {
      const checked = row.channels.includes(channel)
      return (
        <Form.Check
          key={channel}
          id={`reminder-${row.key}-${channel}`}
          className='mb-0'
          label={label}
          aria-label={`${label} ${index + 1}`}
          checked={checked}
          disabled={smsDisabled}
          onChange={(event) =>
            onChange(
              event.currentTarget.checked
                ? CHANNEL_OPTIONS.map((option) => option.channel).filter((candidate) => candidate === channel || row.channels.includes(candidate))
                : row.channels.filter((candidate) => candidate !== channel),
            )
          }
        />
      )
    })}
  </div>
)

interface RowChannelMessageProps {
  channels: NotificationChannel[]
  savedChannels: NotificationChannel[] | null
  smsDisabled: boolean
}

const RowChannelMessage: FC<RowChannelMessageProps> = ({ channels, savedChannels, smsDisabled }) => {
  if (channels.length === 0) {
    return <span className='paragraph-small text-danger'>Choose email, text or both.</span>
  }
  if (smsDisabled) {
    if (savedChannels == null || !savedChannels.includes(NotificationChannel.SMS)) {
      return null
    }
    return (
      <TextsOffWarning>
        {savedChannels.includes(NotificationChannel.EMAIL)
          ? 'Was email and text. Goes out by email only.'
          : "Was text only, so it isn't going out now. Goes out by email once you save."}
      </TextsOffWarning>
    )
  }
  if (!channels.includes(NotificationChannel.EMAIL)) {
    return (
      <span className='d-flex align-items-center gap-2 paragraph-small zocks-text-gray-700'>
        <i aria-hidden='true' className='bi bi-info-circle flex-shrink-0 zocks-text-gray-900' />
        Clients who haven&apos;t opted in to texts won&apos;t get this reminder.
      </span>
    )
  }
  return null
}

interface TextMessagesLinkProps {
  onShowTextMessages: () => void
}

const TextMessagesLink: FC<TextMessagesLinkProps> = ({ onShowTextMessages }) => (
  <Button className='d-inline p-0 align-baseline zocks-text-primary text-decoration-underline' onClick={onShowTextMessages}>
    Text messages
  </Button>
)

interface SmsChannelHelperProps {
  smsChannel: SmsChannelState
  isEditing: boolean
  textsOffWithSmsEntries: boolean
  onShowTextMessages: () => void
}

const SmsChannelHelper: FC<SmsChannelHelperProps> = ({ smsChannel, isEditing, textsOffWithSmsEntries, onShowTextMessages }) => {
  if (textsOffWithSmsEntries && isEditing) {
    return <span className='paragraph-small zocks-text-gray-600'>Reminders go out within 30 minutes of the set time.</span>
  }
  return smsChannel === 'on' || textsOffWithSmsEntries ? (
    <span className='paragraph-small zocks-text-gray-600'>Reminders go out within 30 minutes of the set time. Texts aren&apos;t sent during quiet hours.</span>
  ) : (
    <span className='paragraph-small zocks-text-gray-600'>
      Reminders go out within 30 minutes of the set time. Text reminders are turned off. You can turn them on in{' '}
      <TextMessagesLink onShowTextMessages={onShowTextMessages} />.
    </span>
  )
}

interface TextsOffCardWarningProps {
  isEditing: boolean
  onShowTextMessages: () => void
}

const TextsOffCardWarning: FC<TextsOffCardWarningProps> = ({ isEditing, onShowTextMessages }) => (
  <TextsOffWarning>
    {isEditing
      ? 'Texts are turned off, so reminders can only go out by email. Reminders that were set to text go out by email once you save. To use texts again, turn them on in '
      : "Texts are turned off, so reminders set to text don't go out by text. Update them here or turn texts back on in "}
    <TextMessagesLink onShowTextMessages={onShowTextMessages} />.
  </TextsOffWarning>
)

const rowPillLabel = (row: ReminderRowDraft, settings: ReminderSettings): string | null => {
  if (row.id == null) {
    return 'New'
  }
  const savedEntry = settings.entries.find((entry) => entry.id === row.id)
  return savedEntry != null && (row.offsetMinutes !== savedEntry.offsetMinutes || !sameChannels(row.channels, savedEntry.channels)) ? 'Changed' : null
}

interface EditableRowsProps {
  copy: ReminderSettingsCopy
  settings: ReminderSettings
  draft: ReminderSettingsDraft
  smsChannel: SmsChannelState
  onDraftChange: (draft: ReminderSettingsDraft) => void
}

const EditableRows: FC<EditableRowsProps> = ({ copy, settings, draft, smsChannel, onDraftChange }) => {
  const showChannels = smsChannel !== 'hidden'
  const smsDisabled = smsChannel === 'off'
  const pillBaseline = smsDisabled ? withoutSmsChannel(settings) : settings
  const usedOffsets = draft.rows.map((row) => row.offsetMinutes)
  const removeDisabled = draft.rows.length === 1
  const updateRow = (key: string, change: Partial<Pick<ReminderRowDraft, 'offsetMinutes' | 'channels'>>) =>
    onDraftChange({ ...draft, rows: draft.rows.map((row) => (row.key === key ? { ...row, ...change } : row)) })
  const removeRow = (key: string) => onDraftChange({ ...draft, rows: draft.rows.filter((row) => row.key !== key) })
  const addRow = () => onDraftChange({ ...draft, rows: [...draft.rows, createRowDraft(nextReminderOffset(settings.allowedOffsets, draft.rows))] })
  return (
    <div className='d-flex flex-column gap-2'>
      <div className={classNames(styles.reminderTimeline, 'd-flex flex-column gap-2')}>
        {draft.rows.map((row, index) => {
          const pillLabel = rowPillLabel(row, pillBaseline)
          return (
            <div key={row.key} className='d-flex align-items-center gap-2'>
              <span className={styles.reminderTimelineDot} />
              <div className='flex-grow-1 d-flex flex-column gap-2 zocks-bg-color-gray-100 rounded-2 p-3'>
                <div className='d-flex align-items-center gap-2'>
                  <span>{copy.rowLabel}</span>
                  <Form.Select
                    className='w-auto'
                    aria-label={`${copy.rowLabel} ${index + 1}`}
                    value={row.offsetMinutes}
                    onChange={(event) => updateRow(row.key, { offsetMinutes: Number(event.currentTarget.value) })}
                  >
                    {settings.allowedOffsets.map((offset) => (
                      <option key={offset} value={offset} disabled={offset !== row.offsetMinutes && usedOffsets.includes(offset)}>
                        {formatReminderOffset(offset)}
                      </option>
                    ))}
                  </Form.Select>
                  {showChannels && copy.channelsTrailingCopy != null ? (
                    <>
                      <span>{copy.channelsTrailingCopy}</span>
                      <ChannelCheckboxes row={row} index={index} smsDisabled={smsDisabled} onChange={(channels) => updateRow(row.key, { channels })} />
                      <span className='flex-grow-1' />
                    </>
                  ) : (
                    <span className='flex-grow-1'>{copy.trailingCopy}</span>
                  )}
                  {pillLabel != null ? (
                    <Badge
                      bg='light'
                      className={classNames(
                        'rounded-pill px-2 py-1 fw-normal',
                        pillLabel === 'New' ? 'zocks-bg-color-primary-light zocks-text-primary' : 'border zocks-text-gray-900',
                      )}
                    >
                      {pillLabel}
                    </Badge>
                  ) : null}
                  <Tooltip title={removeDisabled ? copy.removeLastTooltip : undefined}>
                    <CloseButton aria-label={`${copy.removeLabel} ${index + 1}`} disabled={removeDisabled} onClick={() => removeRow(row.key)} />
                  </Tooltip>
                </div>
                {showChannels ? (
                  <RowChannelMessage
                    channels={row.channels}
                    savedChannels={settings.entries.find((entry) => entry.id === row.id)?.channels ?? null}
                    smsDisabled={smsDisabled}
                  />
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
      <div className='d-flex align-items-center gap-2 ms-3'>
        <Button variant='primaryLinkButton' className='p-0 text-decoration-none' disabled={draft.rows.length >= MAX_REMINDER_ENTRIES} onClick={addRow}>
          <i className='bi bi-plus-circle me-1' />
          {copy.addLabel}
        </Button>
        <span className='paragraph-small zocks-text-gray-600'>{`${draft.rows.length} of ${MAX_REMINDER_ENTRIES} added`}</span>
      </div>
    </div>
  )
}

interface EditFooterProps {
  draft: ReminderSettingsDraft
  dirty: boolean
  saveDisabled: boolean
  isSaving: boolean
  onCancel: () => void
  onSave: () => void
}

const EditFooter: FC<EditFooterProps> = ({ draft, dirty, saveDisabled, isSaving, onCancel, onSave }) => (
  <div className='d-flex align-items-end justify-content-between gap-3'>
    <div className='d-flex flex-column gap-1 paragraph-small zocks-text-gray-600'>
      {dirty ? (
        <span>
          <span className='zocks-text-primary'>•</span> Unsaved changes
        </span>
      ) : null}
      {draft.rows.some((row) => row.id == null) ? <span>{NEW_ROW_CAVEAT}</span> : null}
    </div>
    <div className='d-flex gap-2'>
      <Button variant='grayButton' onClick={onCancel}>
        Cancel
      </Button>
      <Button variant='primary' disabled={!dirty || saveDisabled || isSaving} onClick={onSave}>
        Save changes
      </Button>
    </div>
  </div>
)

export const ReminderSettingsCard: FC<ReminderSettingsCardProps> = ({
  copy,
  settings,
  draft,
  smsChannel,
  templatesLabel,
  readOnly,
  editDisabled,
  editDisabledTooltip,
  isSaving,
  error,
  onEdit,
  onCancel,
  onSave,
  onDraftChange,
  onShowMessageTemplates,
  onShowTextMessages,
}) => {
  const dirty = draft != null && settingsChanged(draft, settings)
  const showChannels = smsChannel === 'on'
  const textsOffWithSmsEntries = smsChannel === 'off' && hasSmsEntry(settings)
  return (
    <div className='d-flex flex-column gap-2'>
      <h6 className='mb-0'>{copy.title}</h6>
      <SettingsCard>
        <div className='p-4 d-flex flex-column gap-3'>
          <div className='d-flex align-items-start justify-content-between gap-3'>
            <div className='d-flex flex-column'>
              <span className='bold'>{copy.heading}</span>
              <span className='paragraph-small zocks-text-gray-600'>
                {copy.description}
                {templatesLabel ? (
                  <>
                    {' '}
                    {showChannels ? 'See which messages are sent in' : 'See which email is sent in'}{' '}
                    <Button className='d-inline p-0 align-baseline zocks-text-primary text-decoration-underline' onClick={onShowMessageTemplates}>
                      {templatesLabel}
                    </Button>
                    .
                  </>
                ) : null}
              </span>
              {textsOffWithSmsEntries ? (
                <div className='mt-2'>
                  <TextsOffCardWarning isEditing={draft != null} onShowTextMessages={onShowTextMessages} />
                </div>
              ) : null}
            </div>
            {draft == null ? (
              <div className='d-flex align-items-center gap-1 flex-shrink-0'>
                <span className='paragraph-small zocks-text-gray-700'>{settings.enabled ? 'On' : 'Off'}</span>
                {readOnly ? (
                  <Tooltip title='This setting is managed at the account level'>
                    <i className='bi bi-info-circle' />
                  </Tooltip>
                ) : null}
              </div>
            ) : (
              <Form.Switch
                className='flex-shrink-0'
                aria-label={copy.heading}
                checked={draft.enabled}
                onChange={(event) => onDraftChange({ ...draft, enabled: event.currentTarget.checked })}
              />
            )}
          </div>
          {copy.banner != null ? (
            <div className='d-flex align-items-center gap-2 rounded-2 p-3 zocks-bg-color-primary-light paragraph-small'>
              <i className='bi bi-info-circle flex-shrink-0' />
              {copy.banner}
            </div>
          ) : null}
          {draft == null ? (
            <ReadOnlyRows copy={copy} settings={settings} showChannels={showChannels || textsOffWithSmsEntries} textsOff={textsOffWithSmsEntries} />
          ) : (
            <EditableRows copy={copy} settings={settings} draft={draft} smsChannel={smsChannel} onDraftChange={onDraftChange} />
          )}
          {smsChannel !== 'hidden' ? (
            <SmsChannelHelper
              smsChannel={smsChannel}
              isEditing={draft != null}
              textsOffWithSmsEntries={textsOffWithSmsEntries}
              onShowTextMessages={onShowTextMessages}
            />
          ) : null}
          {draft != null ? (
            <EditFooter draft={draft} dirty={dirty} saveDisabled={hasRowWithoutChannel(draft)} isSaving={isSaving} onCancel={onCancel} onSave={onSave} />
          ) : null}
          {error != null ? <div className='paragraph-small text-danger'>{error}</div> : null}
          {draft == null && !readOnly ? (
            <Tooltip title={editDisabled ? editDisabledTooltip : undefined}>
              <Button variant='primaryLinkButton' className='p-0 text-decoration-none align-self-start' disabled={editDisabled} onClick={onEdit}>
                <i className='bi bi-pencil me-1' />
                Edit
              </Button>
            </Tooltip>
          ) : null}
        </div>
      </SettingsCard>
    </div>
  )
}
